/**
 * CSRF (Cross-Site Request Forgery) Token Management Utilities
 *
 * IMPORTANT: This is frontend preparation for CSRF protection.
 * Full CSRF protection requires backend implementation.
 *
 * ===== BACKEND REQUIREMENTS =====
 *
 * 1. Token Generation:
 *    - Generate a secure, random CSRF token on login
 *    - Token should be cryptographically random (NOT predictable)
 *    - Recommended: Use crypto.randomBytes() or equivalent
 *
 * 2. Token Delivery:
 *    - Option A (Recommended): Return token in response body on login
 *      Example: { token: "...", refreshToken: "...", csrfToken: "..." }
 *    - Option B: Set token in custom response header
 *      Example: X-CSRF-Token: <token-value>
 *    - Do NOT use cookies for token storage (defeats purpose)
 *
 * 3. Token Validation:
 *    - Validate CSRF token on all state-changing requests (POST, PUT, DELETE, PATCH)
 *    - Accept token in request header: X-CSRF-Token
 *    - Compare token from header with token stored in user session
 *    - Reject requests with missing or invalid CSRF tokens
 *
 * 4. Token Lifecycle:
 *    - Generate new token on login
 *    - Store token server-side (in session or database)
 *    - Optionally rotate token periodically for added security
 *    - Clear token on logout
 *
 * 5. Security Best Practices:
 *    - Use SameSite cookies for session management (SameSite=Strict or Lax)
 *    - Implement proper CORS configuration
 *    - Token should be tied to user session
 *    - Token should expire with session
 *
 * ===== FRONTEND USAGE =====
 *
 * Once backend is implemented:
 *
 * 1. Store token on login:
 *    setCsrfToken(response.data.csrfToken);
 *
 * 2. Token automatically added to requests via axios interceptor
 *
 * 3. Clear token on logout (already handled in auth store)
 */

const CSRF_TOKEN_KEY = 'csrf-token';

/**
 * Store CSRF token in memory (more secure than localStorage)
 * In production, consider using sessionStorage if needed
 */
let csrfToken: string | null = null;

/**
 * Set CSRF token
 * Call this after successful login when backend returns the token
 */
export function setCsrfToken(token: string): void {
  csrfToken = token;
  // Optionally store in sessionStorage for persistence across page refreshes
  // sessionStorage.setItem(CSRF_TOKEN_KEY, token);
}

/**
 * Get CSRF token
 * Returns null if token not set
 */
export function getCsrfToken(): string | null {
  // If token in memory, return it
  if (csrfToken) {
    return csrfToken;
  }

  // Optionally check sessionStorage
  // return sessionStorage.getItem(CSRF_TOKEN_KEY);

  return null;
}

/**
 * Clear CSRF token
 * Call this on logout
 */
export function clearCsrfToken(): void {
  csrfToken = null;
  // sessionStorage.removeItem(CSRF_TOKEN_KEY);
}

/**
 * Check if CSRF token exists
 */
export function hasCsrfToken(): boolean {
  return getCsrfToken() !== null;
}

/**
 * Generate CSRF token header
 * Returns header object to include in axios requests
 */
export function getCsrfHeader(): Record<string, string> {
  const token = getCsrfToken();
  if (!token) {
    return {};
  }

  return {
    'X-CSRF-Token': token,
  };
}
